import React, { useState, useEffect } from 'react';
import { LoginModal } from './LoginModal';
import { SignUpModal } from './SignUpModal';
import { ForgotPasswordModal } from './ForgotPasswordModal';

type AuthModalType = 'login' | 'signup' | 'forgot-password';

interface AuthModalManagerProps {
  isOpen: boolean;
  onClose: () => void;
  initialModal?: AuthModalType;
  /** Zie LoginModal: in de native app valt er niets te sluiten. */
  dismissible?: boolean;
}

export const AuthModalManager: React.FC<AuthModalManagerProps> = ({
  isOpen,
  onClose,
  initialModal = 'login',
  dismissible = true
}) => {
  const [currentModal, setCurrentModal] = useState<AuthModalType>(initialModal);
  const [isModalOpen, setIsModalOpen] = useState(isOpen);

  // Update currentModal when initialModal prop changes
  useEffect(() => {
    if (isOpen) {
      setCurrentModal(initialModal);
    }
    setIsModalOpen(isOpen);
  }, [initialModal, isOpen]);

  // Listen for custom events to open modals
  useEffect(() => {
    const handleOpenLoginModal = () => {
      setCurrentModal('login');
      setIsModalOpen(true);
    };

    const handleOpenForgotPasswordModal = () => {
      setCurrentModal('forgot-password');
      setIsModalOpen(true);
    };

    window.addEventListener('openLoginModal', handleOpenLoginModal);
    window.addEventListener('openForgotPasswordModal', handleOpenForgotPasswordModal);

    return () => {
      window.removeEventListener('openLoginModal', handleOpenLoginModal);
      window.removeEventListener('openForgotPasswordModal', handleOpenForgotPasswordModal);
    };
  }, []);

  const handleClose = () => {
    setCurrentModal('login'); // Reset to login modal when closing
    setIsModalOpen(false);
    onClose();
  };

  const switchToLogin = () => setCurrentModal('login');
  const switchToSignup = () => setCurrentModal('signup');
  const switchToForgotPassword = () => setCurrentModal('forgot-password');

  return (
    <>
      <LoginModal
        isOpen={isModalOpen && currentModal === 'login'}
        onClose={handleClose}
        onSwitchToSignup={switchToSignup}
        onSwitchToForgotPassword={switchToForgotPassword}
        dismissible={dismissible}
      />

      <SignUpModal
        isOpen={isModalOpen && currentModal === 'signup'}
        onClose={handleClose}
        onSwitchToLogin={switchToLogin}
        dismissible={dismissible}
      />

      <ForgotPasswordModal
        isOpen={isModalOpen && currentModal === 'forgot-password'}
        onClose={handleClose}
        onSwitchToLogin={switchToLogin}
        dismissible={dismissible}
      />
    </>
  );
};